/* eslint-disable max-len */
import {Box, Grid, Skeleton, Typography} from '@mui/material';
import axios from 'axios';
import {cloneDeep, isArray} from 'lodash';
import {useSnackbar} from 'notistack';
import {forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import {AuthContext} from 'src/store/Auth/AuthContext';
import {v2InfoUpdate, v2PullDroppedItem} from 'src/store/redux/features/backup-location-config';
import {RootState} from 'src/store/redux/store';
import useQueryString from 'src/utility/CustomHooks/useQueryString';
import {Board} from 'src/containers/app/LogicsEngine/components/board';
import {Group} from 'src/containers/app/LogicsEngine/components/board/Group';
import {Card} from 'src/containers/app/LogicsEngine/components/board/Card';
import {DropResult, ResponderProvided} from 'react-beautiful-dnd';
import {
  DEVICE_POSITIONS,
  removeDevice,
  updateConfigurableContainer,
} from 'src/store/redux/features/configurable-containers';
import ConfigurableDeviceContainer from '../ConfigurableDeviceContainer';
import DeviceContainer, {DEVICE_CONTAINER_LOC} from '../shared/DeviceContainer';
import Device, {DEVICE_STATUS_IN_RESTORE_STEP} from '../shared/Device';
import useHideSelectedDevices from '../shared/hooks/useHideSelectedDevices';
import {useRestoreType} from '../shared/hooks/useRestoreType';
import { useQueryStringFirst } from 'src/utility/CustomHooks/useQueryNumber';
import { RestoreDone } from '../shared/RestoreDoneScreen';

export const SessionRestoreCloudDevices = forwardRef((props, ref) => {
  const sessionId = useQueryStringFirst('sessionId');
  const {
    checkedDevicesState,
    allSelectedState,
    hiddenItems,
    showCheckBoxState,
    configurableContainerRef,
    configurableList,
    devicePendingComp,
    isStepCompleted,
  } = useRestoreType('cintd', sessionId);

  const {checkedDevices, setCheckDevices} = checkedDevicesState;
  const {allSelected, setAllSelected} = allSelectedState;

  const cloudDevicePendingComp = devicePendingComp;

  const [_, groupCheckedDevicesByContId, showHiddens, setShowHiddens, forceHide] =
    useHideSelectedDevices(cloudDevicePendingComp, checkedDevices, setCheckDevices);

  const dispatch = useDispatch();
  const {enqueueSnackbar} = useSnackbar();

  const {showCheckBox, setShowCheckBox} = showCheckBoxState;

  useImperativeHandle(ref, () => ({
    getConfigurableContainerRef() {
      return configurableContainerRef;
    },
  }));

  const onDragEnd = (result: DropResult, provided: ResponderProvided) => {
    try {
      console.log(result);
      const {destination} = result;
      if (!destination) return;

      const {droppableId} = destination;

      // dropped on to the existng item. So update existing item list
      // NOTE: cloud device cannot dropped as new or on to available devices like in air
      if (droppableId.indexOf('configExistingDevice') > -1) {
        const existingDeviceId = droppableId.split('configExistingDevice')[1];
        const configurableContainerId = existingDeviceId.split('_')[1];
        const container = configurableList[configurableContainerId];

        const [flag, droppedDedviceId] = result.draggableId.split('___');

        let device = null;
        switch (flag) {
          case 'template_cint_device': {
            const deviceRoot =
              cloudDevicePendingComp.pendingComps[droppedDedviceId.split('_')[1]][droppedDedviceId];
            if (deviceRoot) device = {...deviceRoot.data, id: droppedDedviceId};
            break;
          }
          default:
            return;
        }

        if (device) {
          dispatch(
            updateConfigurableContainer({
              item: {
                deviceMap: {
                  asNew: container.deviceMap.asNew,
                  toExisting: {
                    ...container.deviceMap.toExisting,
                    [existingDeviceId]: {
                      device,
                      mappings: {
                        pin: null,
                        channel: null,
                      },
                    },
                  },
                },
              },
              key: container.dc.id,
              flag: 'UPDATE',
            })
          );
          console.log({
            dropped_contatainer_id: container.dc.id,
            dropped_on: existingDeviceId,
          });
          dispatch(
            v2InfoUpdate({
              value: {
                ...cloudDevicePendingComp,
                pendingComps: {
                  ...cloudDevicePendingComp.pendingComps,
                  [droppedDedviceId.split('_')[1]]: {
                    ...cloudDevicePendingComp.pendingComps[droppedDedviceId.split('_')[1]],
                    [droppedDedviceId]: {
                      ...cloudDevicePendingComp.pendingComps[droppedDedviceId.split('_')[1]][
                        droppedDedviceId
                      ],
                      clientSideMeta: {
                        dropped_contatainer_id: container.dc.id,
                        dropped_on: existingDeviceId,
                      },
                    },
                  },
                },
              },
              key: 'cintd',
              mergeErrorComps: false,
            })
          );
        }
      }
    } catch (err: any) {
      enqueueSnackbar(err.message, {variant: 'error'});
      throw err;
    }
  };

  if (!cloudDevicePendingComp) {
    return (
      <Grid container spacing={1}>
        <Grid item md={6}>
          <Skeleton sx={{bgcolor: '#D9DFE0'}} variant="rectangular" width="100%" height={200} />
        </Grid>
      </Grid>
    );
  }

  if (isStepCompleted) {
    return <RestoreDone />;
  }

  return (
    <Board onDragEnd={onDragEnd}>
      <Grid container spacing={1}>
        <Grid item md={6}>
          {cloudDevicePendingComp && cloudDevicePendingComp.pendingComps && (
            <>
              {cloudDevicePendingComp.pendingComps &&
                Object.keys(cloudDevicePendingComp.pendingComps).map(contId => {
                  const container = cloudDevicePendingComp.pendingComps[contId];
                  const containerInfo = cloudDevicePendingComp.collections[contId];
                  return (
                    <>
                      <DeviceContainer
                        title={containerInfo ? containerInfo.name : 'Template Cloud Container'}
                        position={DEVICE_CONTAINER_LOC.IN_TEMPLATE}
                        allSelected={allSelected && allSelected[contId]}
                        onToggleAllSelect={checked =>
                          setAllSelected(state => ({
                            // ...state,
                            [contId]: checked,
                          }))
                        }
                        checkedDevices={checkedDevices[contId] ? checkedDevices[contId] : {}}
                        hideSelectedDevices={groupCheckedDevicesByContId}
                        hasHiddenItems={
                          hiddenItems &&
                          hiddenItems[contId] &&
                          Object.keys(hiddenItems[contId]).length > 0
                        }
                        showHiddenItems={showHiddens}
                        onToggleShowHiddenItems={setShowHiddens}
                        type="cintd"
                        dcId={contId}
                      >
                        {({searchText, devices}) => (
                          <Group
                            droppableId={`template_cint_dc_container_${contId}`}
                            droppableType="cintd" // TODO: maybe change this based on the cint type eg: 'cint_shelly', 'cint_enode' etc
                            isDropDisabled
                          >
                            {() => (
                              <Grid container spacing={1}>
                                {Object.keys(devices).map(key => {
                                  const cintDevice = devices[key];

                                  if (searchText && searchText.length > 0) {
                                    if (
                                      !(cintDevice.data.name.toLowerCase().indexOf(searchText) > -1)
                                    ) {
                                      return <></>;
                                    }
                                  }
                                  return (
                                    <Grid item md={3} key={cintDevice.old_id}>
                                      <Card
                                        draggableId={`template_cint_device___${cintDevice.old_id}`}
                                        index={1}
                                        dragDisabled={
                                          !!(
                                            cintDevice.clientSideMeta &&
                                            cintDevice.clientSideMeta.dropped_contatainer_id
                                          )
                                        }
                                      >
                                        {({provided}) => (
                                          <Box>
                                            <Device
                                              deviceStatus={
                                                DEVICE_STATUS_IN_RESTORE_STEP.IN_TEMPLATE_DEVICE
                                              }
                                              isHidden={
                                                hiddenItems &&
                                                hiddenItems[contId] &&
                                                hiddenItems[contId][key]
                                              }
                                              device={cintDevice.data as any}
                                              dragHandlerProps={{...provided.dragHandleProps}}
                                              hasError={cintDevice.clone_status === 'e'}
                                              dragDisabled={
                                                !!(
                                                  cintDevice.clientSideMeta &&
                                                  cintDevice.clientSideMeta.dropped_contatainer_id
                                                )
                                              }
                                              bgColor={
                                                cintDevice.clientSideMeta &&
                                                cintDevice.clientSideMeta.dropped_contatainer_id
                                                  ? '#d6d6d6'
                                                  : null
                                              }
                                              onLongPress={() => setShowCheckBox(!showCheckBox)}
                                              onClickUnHide={() => {
                                                console.log(hiddenItems);
                                                if (
                                                  hiddenItems &&
                                                  Object.keys(hiddenItems).length > 0
                                                ) {
                                                  const cpy = cloneDeep(hiddenItems);
                                                  delete cpy[contId][cintDevice.old_id];
                                                  console.log(cpy);
                                                  forceHide(cpy);
                                                }
                                                // setCheckDevices({ ...cpy });
                                              }}
                                              showCheckBox
                                              checked={
                                                checkedDevices[contId] &&
                                                checkedDevices[contId][cintDevice.old_id]
                                              }
                                              handleCheckChange={(checked: boolean) => {
                                                // if manually clicked, reset allCheck,
                                                setAllSelected(null);
                                                if (!checked) {
                                                  const cpy = cloneDeep(checkedDevices);
                                                  delete cpy[contId][cintDevice.old_id];

                                                  setCheckDevices({...cpy});
                                                } else {
                                                  setCheckDevices({
                                                    ...checkedDevices,
                                                    [contId]: {
                                                      ...checkedDevices[contId],
                                                      [cintDevice.old_id]: checked,
                                                    },
                                                  });
                                                }
                                              }}
                                              // eslint-disable-next-line consistent-return
                                              handleOnPullClick={() => {
                                                if (!cintDevice.clientSideMeta) return null;

                                                if (
                                                  cintDevice.clientSideMeta.dropped_on &&
                                                  cintDevice.clientSideMeta.dropped_on.startsWith(
                                                    DEVICE_POSITIONS.TO_AVAILABLE
                                                  )
                                                ) {
                                                  dispatch(
                                                    removeDevice({
                                                      type: DEVICE_POSITIONS.TO_AVAILABLE,
                                                      configurableDcId:
                                                        cintDevice.clientSideMeta
                                                          .dropped_contatainer_id,
                                                      key: cintDevice.clientSideMeta.dropped_on,
                                                    })
                                                  );
                                                } else if (cintDevice.clientSideMeta.dropped_on) {
                                                  dispatch(
                                                    removeDevice({
                                                      type: DEVICE_POSITIONS.TO_EXISTING,
                                                      configurableDcId:
                                                        cintDevice.clientSideMeta
                                                          .dropped_contatainer_id,
                                                      key: cintDevice.clientSideMeta.dropped_on,
                                                    })
                                                  );
                                                } else {
                                                  dispatch(
                                                    removeDevice({
                                                      type: DEVICE_POSITIONS.AS_NEW,
                                                      configurableDcId:
                                                        cintDevice.clientSideMeta
                                                          .dropped_contatainer_id,
                                                      key: cintDevice.old_id,
                                                    })
                                                  );
                                                }

                                                dispatch(
                                                  v2PullDroppedItem({
                                                    type: 'cintd',
                                                    dcId: contId,
                                                    deviceId: cintDevice.old_id,
                                                  })
                                                );
                                              }}
                                            />
                                          </Box>
                                        )}
                                      </Card>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            )}
                          </Group>
                        )}
                      </DeviceContainer>
                      <Box style={{height: 10}} />
                    </>
                  );
                })}
            </>
          )}
        </Grid>
        <Grid item md={6}>
          <ConfigurableDeviceContainer type="cintd" dcCat={2} ref={configurableContainerRef} />
        </Grid>
      </Grid>
    </Board>
  );
});

export default SessionRestoreCloudDevices;
